import { Link } from "react-router-dom";
import { Shield, ShieldCheck, Crown, AlertTriangle, ArrowRight, Lock, Check } from "lucide-react";

const PLANS = [
  {
    id: "minimum",
    name: "Básica",
    tagline: "Protección Esencial",
    accent: "border-neutral-200",
    icon: Shield,
    iconColor: "text-neutral-600",
    rule: "$250 MXN o 10% del total del viaje, lo que sea mayor",
    benefits: [
      "Soporte RivoNes 24/7",
      "Asistencia en carretera",
      "Verificación de identidad y licencia",
      "Gestión básica de incidentes",
      "Depósito reembolsable de $20,000 MXN",
    ],
    note: "Ayuda a reducir la exposición financiera. Sujeto a términos, exclusiones, inspección y revisión de reclamación.",
  },
  {
    id: "standard",
    name: "Estándar",
    tagline: "Más Protección y Menor Depósito",
    accent: "border-terracotta",
    icon: ShieldCheck,
    iconColor: "text-terracotta",
    rule: "$350 MXN o 15% del total del viaje, lo que sea mayor",
    benefits: [
      "Todo lo incluido en Básica",
      "Soporte prioritario",
      "Gestión acelerada de incidentes",
      "Mejor respaldo en incidentes elegibles",
      "Depósito reembolsable de $10,000 MXN",
    ],
    note: "Mayor protección que el plan Básica. Sujeto a términos, exclusiones, inspección y revisión de reclamación.",
  },
  {
    id: "premier",
    name: "Premium",
    tagline: "Sin Depósito y Atención VIP",
    accent: "border-amber-400",
    icon: Crown,
    iconColor: "text-amber-500",
    rule: "$500 MXN o 20% del total del viaje, lo que sea mayor",
    benefits: [
      "Todo lo incluido en Estándar",
      "La opción de protección más alta disponible",
      "Diseñado para huéspedes que buscan la máxima protección disponible",
      "Puede reducir o evitar ciertas retenciones de depósito (cuando elegible)",
      "Beneficios exclusivos RivoNes",
    ],
    locked: true,
    note: "Sujeto a términos, exclusiones, inspección y revisión de reclamación.",
  },
];

const VOID_REASONS = [
  "El huésped conduce bajo influencia de alcohol, drogas o sustancias ilegales",
  "El vehículo se utiliza para actividades ilegales",
  "El vehículo es conducido por un conductor no autorizado",
  "El vehículo se lleva fuera de las zonas aprobadas sin permiso",
  "El vehículo se usa para carreras, off-road indebido, remolque, delivery comercial, rideshare u otro uso prohibido",
  "El huésped no reporta inmediatamente daños, robo, accidente o incidente",
  "El huésped provee información falsa o documentos fraudulentos",
  "El huésped deshabilita el GPS, sistemas de rastreo, seguridad o anti-manipulación",
  "El huésped viola el contrato de renta, los términos de servicio o las reglas del anfitrión",
  "El vehículo se devuelve con daño intencional, negligencia, abuso excesivo o partes faltantes",
];

export default function PlanesProteccionPage() {
  return (
    <div className="min-h-screen bg-sand pb-16" data-testid="planes-proteccion-page">
      <div className="container-main py-10 max-w-4xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center">
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold text-agave">Planes de Protección</h1>
            <p className="text-sm text-neutral-600">
              Programas de asistencia y soporte operativo para tu tranquilidad en cada viaje.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {PLANS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className={`relative bg-white rounded-2xl p-5 border-2 ${p.accent} flex flex-col`}
                data-testid={`plan-card-${p.id}`}
              >
                {p.locked && (
                  <div className="absolute top-3 right-3 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Elegibilidad
                  </div>
                )}
                <Icon className={`w-8 h-8 ${p.iconColor} mb-3`} />
                <h2 className="font-heading font-bold text-xl text-agave">{p.name}</h2>
                <p className="text-xs text-terracotta font-medium mb-3">{p.tagline}</p>
                <p className="text-sm font-bold text-agave mb-1">{p.rule}</p>
                <ul className="mt-3 space-y-1.5 flex-1">
                  {p.benefits.map((b, i) => (
                    <li key={i} className="text-xs text-neutral-700 flex items-start gap-1.5">
                      <Check className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-neutral-500 italic mt-3">{p.note}</p>
              </div>
            );
          })}
        </div>

        {/* Deposits explanation */}
        <section className="card-base mb-6">
          <h2 className="font-heading font-semibold text-lg text-agave mb-2">¿Cómo funcionan los depósitos?</h2>
          <p className="text-sm text-neutral-700 leading-relaxed">
            Los depósitos no se cobran como cargo final al momento de la reservación. Se colocan como retención
            temporal y se liberan una vez que el vehículo sea devuelto correctamente, sujeto también a los
            tiempos del banco emisor.
          </p>
        </section>

        {/* When protection is void */}
        <section className="card-base mb-6 bg-red-50/40 border-red-200" data-testid="protection-void-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h2 className="font-heading font-semibold text-lg text-agave">Cuándo se anula la protección</h2>
          </div>
          <p className="text-sm text-neutral-700 mb-3">La protección puede volverse inválida si:</p>
          <ul className="space-y-2 text-sm">
            {VOID_REASONS.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-red-600 font-bold flex-shrink-0">×</span>
                <span className="text-neutral-700 leading-snug">{r}</span>
              </li>
            ))}
          </ul>
        </section>

        <p className="text-xs text-neutral-500 italic mb-6">
          Los planes de protección no son una póliza de seguro independiente. Son beneficios de protección
          sujetos a los términos, condiciones, exclusiones, revisión de evidencia, inspección del vehículo y
          aprobación de reclamación.
        </p>

        <Link to="/search" className="btn-primary inline-flex items-center gap-2" data-testid="planes-cta">
          Reservar con Protección
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
